// components/bundles/BundleComposer.js
import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  Wand2, CheckCircle2, Copy, Loader2, Info, ExternalLink,
  Save, ClipboardCopy, ChevronDown, X, ChevronRight, SlidersHorizontal,
  Lightbulb, ListPlus, Edit, Check, Search, ArrowRight, ArrowLeft, Plus, Sparkles,
  Trash2, PackagePlus, BarChart3, Package, Eye, Code2, RefreshCw,
  Video, // --- ADDED Video icon
  MousePointerClick // --- ADDED new icon
} from 'lucide-react';

// Utils
import {
  proposeBundles, createBundleTitle, buildBundlePrompts, formatExportForTPT,
  buildBundleTitlePrompt, getBuckets, prettyGrade, buildWhyItWorks,
  buildDescriptionAnalysisPrompt, buildRefinePrompt // NEW: Import refine prompt
} from '../../utils/bundles';

// Firebase
import { db, analytics, functionsInstance } from '../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  collection, addDoc, serverTimestamp, getDocs,
  query, where, orderBy, limit,
  doc, setDoc, deleteDoc
} from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';

// --- NEW: Storylane script URL ---
const STORYLANE_SCRIPT_URL = "https://js.storylane.io/js/v2/storylane.js";

/* ---------- display constants ---------- */
const SUGGESTION_INITIAL_LIMIT = 3;
const SUGGESTION_TOTAL_LIMIT = 10;
const SAVED_QUERY_LIMIT = 50;
const SAVED_SHOW_LIMIT  = 15;

/* *******************************************************************
 * --- Core Helpers ---
 ******************************************************************* */

const makeBundleKey = (bundle) => {
  const ids = (bundle.products || [])
    .map(p => p.id || p.productId || p.tptId || p.slug || '')
    .filter(Boolean)
    .sort();
  return ids.join('|');
};

// buildWhyItWorks is now imported from utils/bundles.js

/* *******************************************************************
 * --- UI Sub-Components (Styled to match SeoRankTracker) ---
 ******************************************************************* */

/* --- UI: TagPill --- */
const TagPill = ({ tone = 'slate', children }) => {
  const tones = {
    purple: 'bg-purple-100 text-purple-800', green: 'bg-green-100 text-green-800',
    blue: 'bg-sky-100 text-sky-800', amber: 'bg-amber-100 text-amber-800',
    slate: 'bg-slate-100 text-slate-800'
  };
  return <span className={`inline-block text-xs font-semibold rounded-full px-2.5 py-1 ${tones[tone]}`}>{children}</span>;
};

/* --- UI: Badge --- */
const Badge = ({ level, className = '' }) => {
  const map = {
    strong: 'bg-green-100 text-green-700 ring-1 ring-green-300',
    good: 'bg-amber-100 text-amber-700 ring-1 ring-amber-300',
    potential: 'bg-slate-100 text-slate-700 ring-1 ring-slate-300',
  };
  const label = level === 'strong' ? 'Strong Match' : level === 'good' ? 'Good Match' : 'Potential';
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[level]} whitespace-nowrap ${className}`}>{label}</span>;
};

/* --- UI: ProductChip --- */
const ProductChip = ({ p, onRemove }) => (
  <div className="inline-flex items-center max-w-full gap-1 pl-2.5 pr-1 py-1 rounded-full bg-white border border-slate-300 text-sm text-slate-800">
    <a href={p.tptUrl || '#'} target={p.tptUrl ? '_blank' : undefined} rel="noopener noreferrer" className="inline-flex items-center gap-1.5 min-w-0" title={p.productTitle || p.title || 'Product'}>
      <span className="truncate max-w-[250px]">{p.productTitle || p.title || 'Untitled'}</span>
      {p.tptUrl && <ExternalLink size={14} className="opacity-60 shrink-0" />}
    </a>
    {onRemove && (
      <button className="ml-1 p-1 hover:bg-red-100 hover:text-red-700 text-slate-500 rounded-full shrink-0" title="Remove" onClick={onRemove}>
        <X size={14} />
      </button>
    )}
  </div>
);

/* --- UI: MultiSelect (for filters) --- */
const MultiSelect = ({ options, selected, onChange, placeholder = 'Select…' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const listener = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', listener);
    return () => document.removeEventListener('click', listener);
  }, []);
  const getVal = (opt) => (typeof opt === 'string' ? opt : opt.value);
  const getLabel = (opt) => {
    const raw = typeof opt === 'string' ? opt : (opt.label ?? opt.value);
    const titleCase = (str) => str.replace(/\b([a-z])/g, m => m.toUpperCase());
    return placeholder.toLowerCase().includes('grade') ? prettyGrade(raw) : titleCase(raw);
  };
  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full px-3 py-2.5 text-left rounded-lg border border-slate-300 bg-white text-sm flex items-center justify-between gap-2 shadow-sm hover:border-slate-400 focus:ring-2 focus:ring-sky-500">
        <span className="truncate">{selected.length ? `${selected.length} selected` : placeholder}</span>
        <ChevronDown size={16} className="opacity-60 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto bg-white border border-slate-200 rounded-lg shadow-xl p-1">
          {options.length === 0 ? <div className="text-xs text-slate-500 p-2">No options</div>
            : options.map(opt => {
              const v = getVal(opt);
              return (
                <label key={v} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" className="rounded text-sky-600 focus:ring-sky-500" checked={selected.includes(v)} onChange={() => toggle(v)} />
                  <span className="text-sm break-words">{getLabel(opt)}</span>
                </label>
              );
            })}
        </div>
      )}
    </div>
  );
};

/* --- UI: MultiSelectDropdown (for inside modal) --- */
const MultiSelectDropdown = ({ options, selectedValues, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const displayLabel = useMemo(() => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const selected = options.find(o => o.value === selectedValues[0]);
      return selected ? selected.label : placeholder;
    }
    return `${selectedValues.length} products selected`;
  }, [selectedValues, options, placeholder]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOption = (value) => {
    const newSelection = selectedValues.includes(value)
      ? selectedValues.filter(item => item !== value)
      : [...selectedValues, value];
    onChange(newSelection);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="w-full p-3 border border-slate-300 rounded-lg bg-white text-left shadow-sm focus:ring-2 focus:ring-sky-500 flex justify-between items-center text-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown size={16} className="opacity-60 shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute z-30 w-full bg-white border border-slate-300 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
          <div className="p-2 border-b border-slate-200 sticky top-0 bg-white">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search products..."
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          {filteredOptions.length === 0 ? (
            <div className="p-3 text-sm text-slate-500">No results found.</div>
          ) : (
            <ul>
              {filteredOptions.map((option) => (
                <li
                  key={option.value}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 flex items-center"
                  onClick={() => handleToggleOption(option.value)}
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    readOnly
                    className="mr-2 h-4 w-4 text-sky-600 border-gray-300 rounded"
                  />
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};


/* --- UI: Saved Bundles Panel (Sidebar) --- */
const SavedBundlesPanel = ({ savedPlans, error, onLoadPlan, onDeletePlan, savedSearch, setSavedSearch }) => {

  const filteredPlans = useMemo(() => {
    if (!savedSearch) return savedPlans;
    const lowerSearch = savedSearch.toLowerCase();
    return savedPlans.filter(plan =>
      plan.title.toLowerCase().includes(lowerSearch)
    );
  }, [savedPlans, savedSearch]);

  return (
    <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-200 sticky top-4">
      <h3 className="text-lg font-semibold text-slate-800 mb-3">Your Saved Bundles</h3>

      <div className="relative mb-3">
        <input
          type="text"
          value={savedSearch}
          onChange={(e) => setSavedSearch(e.target.value)}
          placeholder="Search saved bundles..."
          className="w-full p-2 pl-8 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 text-sm"
        />
        <Search size={16} className="absolute left-2.5 top-2.5 text-slate-400" />
      </div>

      {error ? <p className="text-xs text-rose-600 mt-2">{error.includes('index') ? 'Firestore index is building. Please wait a moment and refresh.' : 'Could not load saved bundles.'}</p>
      : savedPlans.length === 0 ? <p className="text-sm text-slate-500 mt-2">You haven't saved any bundles yet.</p>
      : filteredPlans.length === 0 ? <p className="text-sm text-slate-500 mt-2">No bundles match your search.</p>
      : (
        <ul className="space-y-2 max-h-96 overflow-auto pr-1">
          {filteredPlans.slice(0, SAVED_SHOW_LIMIT).map(row => {
            const productCount = row.productIds?.length || 0;
            return (
              <li key={row.id} className="group relative bg-slate-50 border border-slate-200 rounded-lg">
                <div className="flex justify-between items-center p-3">
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => onLoadPlan(row)}
                    title={`Edit "${row.title}"`}
                  >
                    <span className="text-sm font-semibold text-slate-800 truncate block">{row.title}</span>
                    <div className="text-xs text-slate-500 truncate mt-1">
                      {productCount} {productCount === 1 ? 'Product' : 'Products'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onLoadPlan(row)}
                    className="ml-2 flex-shrink-0 inline-flex items-center gap-1 text-xs text-sky-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Edit size={12} /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeletePlan(row.id, row.title);
                    }}
                    className="ml-1 flex-shrink-0 p-1 text-slate-400 hover:text-red-600 hover:bg-red-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    title={`Delete "${row.title}"`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            );
          })}
          {filteredPlans.length > SAVED_SHOW_LIMIT && (
            <li className="text-xs text-slate-500 text-center mt-1">...and {filteredPlans.length - SAVED_SHOW_LIMIT} more.</li>
          )}
        </ul>
      )}
    </div>
  );
};

/* --- UI: WhyItMatches Dropdown (New) --- */
const WhyItMatchesDropdown = ({ why }) => {
  const { shared, all, noMatch } = why;

  if (noMatch) {
    return <p className="text-slate-700">{noMatch}</p>;
  }

  const renderSection = (title, data) => {
    if (!data || data.length === 0) return null;
    return (
      <div>
        <strong className="text-slate-800 block">{title}:</strong>
        <p className="text-slate-700">{data.join(', ')}</p>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      { (shared.subjects.length > 0 || shared.grades.length > 0 || shared.seasonal.length > 0 || shared.types.length > 0) &&
        <div>
          <strong className="text-slate-800 block">Shared Tags (All Products Match):</strong>
          <div className="pl-2 space-y-1">
            {renderSection('Subjects', shared.subjects)}
            {renderSection('Grades', shared.grades)}
            {renderSection('Seasons', shared.seasonal)}
            {renderSection('Types', shared.types)}
          </div>
        </div>
      }
      <div>
        <strong className="text-slate-800 block">All Tags in Bundle (Full Coverage):</strong>
        <div className="pl-2 space-y-1">
          {renderSection('Subjects', all.subjects)}
          {renderSection('Grades', all.grades)}
          {renderSection('Seasons', all.seasonal)}
          {renderSection('Types', all.types)}
        </div>
      </div>
    </div>
  );
};

/* --- UI: Suggestion Card --- */
const SuggestionCard = ({ suggestion, onUse }) => {
  const s = suggestion;
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const showSubjects = s.matchTags.subjects;
  const showGrades   = s.matchTags.grades;
  const showSeasonal = s.matchTags.seasonal;
  const showTypes    = s.matchTags.types;
  const hasAnyTags =
    showSubjects.length || showGrades.length || showSeasonal.length || showTypes.length;

  return (
    <li className="bg-white border border-slate-200 rounded-xl shadow-lg transition-all hover:shadow-xl">
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge level={s.level} />
            <span className="text-xs text-slate-500">{s.products.length} Products</span>
            <span className="text-xs text-slate-500">Match Strength: {s.score}</span>
          </div>
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold shadow-md transition-colors"
            onClick={() => onUse(s)}
            title="Use this suggestion"
          >
            <Edit size={16} />
            Build with this
          </button>
        </div>
        {hasAnyTags && (
          <div className="flex flex-wrap gap-1.5">
            {showSubjects.map(t => <TagPill key={`sub-${t}`} tone="green">{t}</TagPill>)}
            {showGrades.map(t => <TagPill key={`gr-${t}`} tone="blue">{t}</TagPill>)}
            {showSeasonal.map(t => <TagPill key={`sea-${t}`} tone="purple">{t}</TagPill>)}
            {showTypes.map(t => <TagPill key={`ty-${t}`} tone="amber">{t}</TagPill>)}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {s.products.map(p => (
            <ProductChip key={p.id} p={p} />
          ))}
        </div>
      </div>
      <div className="border-t border-slate-100">
        <button
          className="w-full flex items-center justify-between p-3 text-sm text-slate-700 hover:bg-slate-50 rounded-b-xl"
          onClick={() => setIsDetailsOpen(v => !v)}
        >
          <span className="font-medium">
            {isDetailsOpen ? 'Hide Details' : 'Show Details (Why it matches)'}
          </span>
          {isDetailsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        {isDetailsOpen && (
          <div className="pb-3 px-3 -mt-2 text-sm text-slate-700">
            <WhyItMatchesDropdown why={s.why} />
          </div>
        )}
      </div>
    </li>
  );
};

/* *******************************************************************
 * --- UI: Bundle Title Generator (Inside Modal) ---
 ******************************************************************* */
const BundleTitleGenerator = ({ selectedProducts, onTitleSelect }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [titles, setTitles] = useState([]);
  const [error, setError] = useState('');

  const generateTitles = async () => {
    setIsGenerating(true);
    setError('');
    setTitles([]);
    const prompt = buildBundleTitlePrompt(selectedProducts);

    try {
      const callGeminiCloudFunction = httpsCallable(functionsInstance, 'callGeminiAPI');
      const result = await callGeminiCloudFunction({ prompt, model: "gemini-2.5-flash-lite" });
      const rawText = result.data?.generatedText || '{}';
      const cleanedText = rawText.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(cleanedText);
      const list = Array.isArray(parsed.titles) ? parsed.titles.map(t => t.slice(0, 87)) : [];
      setTitles(list);
      if (list.length === 0) setError("The AI couldn't generate titles. Try again.");
    } catch (err) {
      console.error("Error generating bundle titles:", err);
      setError(`An error occurred: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 mt-2 space-y-3">
      <button
        type="button"
        onClick={generateTitles}
        disabled={isGenerating}
        className="inline-flex items-center gap-2 text-sm font-medium text-sky-600 hover:text-sky-800 disabled:opacity-50"
      >
        {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {isGenerating ? 'Generating Ideas...' : 'Generate Title Ideas'}
      </button>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {titles.length > 0 && (
        <fieldset className="space-y-2">
          <legend className="sr-only">Title suggestions</legend>
          {titles.map((title, index) => (
            <label key={index} className="flex items-center space-x-3 p-2 rounded-md hover:bg-slate-100 cursor-pointer">
              <input
                type="radio"
                name="bundle-title-suggestion"
                value={title}
                onChange={() => onTitleSelect(title)}
                className="h-4 w-4 text-sky-600 border-slate-400 focus:ring-sky-500"
              />
              <span className="font-medium text-slate-700 text-sm">{title}</span>
            </label>
          ))}
          <p className="text-xs text-slate-500 pt-1">
            Select a suggestion to use it. You can edit it in the text box.
          </p>
        </fieldset>
      )}
    </div>
  );
};

/* *******************************************************************
 * --- UI: Builder Modal (The "Pop-out") ---
 ******************************************************************* */
const BuilderModal = ({
  isOpen, onClose, selectedProducts, onRemove, bundleTitle, setBundleTitle,
  bundleCopy, setBundleCopy, generating, saving, onGenerate, onSave,
  showUrls, setShowUrls, currentUser,
  filteredProducts, onAddProduct,
  allProducts,
  analysis, isAnalyzing,
  relatedProducts, setRelatedProducts,
  onRefine, rephraseRequest, setRephraseRequest
}) => {
  const [copied, setCopied] = useState(null);
  const [showAdditions, setShowAdditions] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [showRelated, setShowRelated] = useState(false);
  const [showTitleGen, setShowTitleGen] = useState(false);
  const [outputViewMode, setOutputViewMode] = useState('preview');
  const [showRefine, setShowRefine] = useState(false);

  const handleCopy = async (type, textToCopy) => {
    if (!textToCopy) return;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      alert('Failed to copy to clipboard.');
    }
  };

  const handleTptCopy = () => {
    const text = formatExportForTPT({ title: bundleTitle, body: bundleCopy });
    handleCopy('tpt', text);
  };

  const handleBodyCopy = () => {
    handleCopy('body', bundleCopy);
  };

  const { suggestedAdditions, unmatchedAdditions, allOtherProducts } = useMemo(() => {
    const selectedIds = new Set(selectedProducts.map(p => p.id));
    const filteredList = Array.isArray(filteredProducts) ? filteredProducts : [];
    const matched = filteredList.filter(p => !selectedIds.has(p.id));
    const allList = Array.isArray(allProducts) ? allProducts : [];
    const filteredIds = new Set(filteredList.map(p => p.id));
    const unmatched = allList.filter(p => !selectedIds.has(p.id) && !filteredIds.has(p.id));
    const allOthers = allList
      .filter(p => !selectedIds.has(p.id))
      .map(p => ({ value: p.id, label: p.productTitle || p.title || 'Untitled' }));
    return { suggestedAdditions: matched, unmatchedAdditions: unmatched, allOtherProducts: allOthers };
  }, [filteredProducts, allProducts, selectedProducts]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/20 flex items-center justify-center p-4 font-sans">
      <div className="relative bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-xl flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-800 inline-flex items-center gap-2">
            <Edit size={20} />
            Bundle Builder
          </h2>
          <button onClick={onClose} className="p-1 text-slate-500 hover:text-slate-800" title="Close">
            <X size={24} />
          </button>
        </div>

        {/* Scrolling Content */}
        <div className="p-5 space-y-5 overflow-y-auto">
          {/* Products in bundle */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Products in this bundle ({selectedProducts.length})
            </label>
            <div className="flex flex-wrap gap-1.5">
              {selectedProducts.map(p => (
                <ProductChip key={`builder-${p.id}`} p={p} onRemove={() => onRemove(p.id)} />
              ))}
            </div>
          </div>

          {/* Add More Matched Products Section */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg">
            <button
              className="w-full flex items-center justify-between p-3 text-sm font-medium text-slate-700"
              onClick={() => setShowAdditions(v => !v)}
            >
              <span className="inline-flex items-center gap-2"><Plus size={16} /> Add Matched Products</span>
              {showAdditions ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {showAdditions && (
              <div className="p-3 pt-0">
                {suggestedAdditions.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-auto pr-2">
                    <p className="text-xs text-slate-500 mb-2">
                      These products also matched your filters from Step 1.
                    </p>
                    {suggestedAdditions.map(p => (
                      <div key={p.id} className="flex items-center justify-between gap-2 p-2 bg-white rounded border border-slate-200">
                        <span className="text-sm truncate" title={p.productTitle || p.title}>{p.productTitle || p.title || 'Untitled'}</span>
                        <button
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-sky-100 hover:bg-sky-200 text-sky-700 text-xs font-medium"
                          onClick={() => onAddProduct(p.id)}
                        >
                          <Plus size={14} /> Add
                        </button>
                      </div>
                    ))}
                  </div>
                ) : ( <p className="text-sm text-slate-500">All matched products are already in your bundle!</p> )}
              </div>
            )}
          </div>

          {/* Add Unmatched Products Section */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg">
            <button
              className="w-full flex items-center justify-between p-3 text-sm font-medium text-slate-700"
              onClick={() => setShowUnmatched(v => !v)}
            >
              <span className="inline-flex items-center gap-2"><PackagePlus size={16} /> Add Any Other Product</span>
              {showUnmatched ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {showUnmatched && (
              <div className="p-3 pt-0">
                {unmatchedAdditions.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-auto pr-2">
                    <p className="text-xs text-slate-500 mb-2">
                      These are other products from your hub that did not match your filters.
                    </p>
                    {unmatchedAdditions.map(p => (
                      <div key={p.id} className="flex items-center justify-between gap-2 p-2 bg-white rounded border border-slate-200">
                        <span className="text-sm truncate" title={p.productTitle || p.title}>{p.productTitle || p.title || 'Untitled'}</span>
                        <button
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-sky-100 hover:bg-sky-200 text-sky-700 text-xs font-medium"
                          onClick={() => onAddProduct(p.id)}
                        >
                          <Plus size={14} /> Add
                        </button>
                      </div>
                    ))}
                  </div>
                ) : ( <p className="text-sm text-slate-500">All products from your hub are already included or in the "Matched" list.</p> )}
              </div>
            )}
          </div>

          {/* "You Might Also Like" Section */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg">
            <button
              className="w-full flex items-center justify-between p-3 text-sm font-medium text-slate-700"
              onClick={() => setShowRelated(v => !v)}
            >
              <span className="inline-flex items-center gap-2"><Package size={16} /> Add 'You Might Also Like' Links</span>
              {showRelated ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {showRelated && (
              <div className="p-3 pt-0">
                <p className="text-xs text-slate-500 mb-2">
                  Select other products from your hub to link to at the end of your description.
                </p>
                <MultiSelectDropdown
                  options={allOtherProducts}
                  selectedValues={relatedProducts}
                  onChange={setRelatedProducts}
                  placeholder="Select products to link to..."
                />
              </div>
            )}
          </div>

          {/* Bundle Title & Title Generator */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="bundle-title" className="text-sm font-medium text-slate-700">
                Bundle Title
              </label>
              <div className="flex items-center gap-4">
                <span className={`text-xs ${bundleTitle.length > 87 ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                  {bundleTitle.length} / 87
                </span>
                <button
                  type="button"
                  onClick={() => setShowTitleGen(v => !v)}
                  className="text-xs text-sky-600 hover:text-sky-800 font-medium inline-flex items-center gap-1"
                >
                  {showTitleGen ? <X size={14} /> : <Sparkles size={14} />}
                  {showTitleGen ? 'Hide Generator' : 'Generate Ideas'}
                </button>
              </div>
            </div>
            <input
              id="bundle-title"
              className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 text-sm"
              placeholder="e.g., 6th Grade Fractions Mastery Bundle"
              value={bundleTitle}
              onChange={(e) => setBundleTitle(e.target.value)}
            />
            {showTitleGen && (
              <BundleTitleGenerator
                selectedProducts={selectedProducts}
                onTitleSelect={(title) => {
                  setBundleTitle(title);
                  setShowTitleGen(false);
                }}
              />
            )}
          </div>

          {/* Generate Button */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <button
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold shadow-md transition-colors"
              onClick={onGenerate}
              disabled={generating}
            >
              {generating ? <Loader2 className="animate-spin" size={18} /> : <Wand2 size={18} />}
              {generating ? 'Generating…' : 'Generate Description'}
            </button>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 whitespace-nowrap">
              <input type="checkbox" className="rounded text-sky-600" checked={showUrls} onChange={e => setShowUrls(e.target.checked)} />
              Include 'What's Included' URLs
            </label>
          </div>

          {/* Analysis "Greatness" Preview (Smaller) */}
          {isAnalyzing && (
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-slate-600 text-sm flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" size={16} />
              Analyzing description quality...
            </div>
          )}
          {analysis && !isAnalyzing && (
            <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg space-y-2">
              <h4 className="text-sm font-semibold text-sky-700 flex items-center gap-2">
                <BarChart3 size={16} />
                Description Analysis
              </h4>
              <div className="space-y-1">
                <p className="text-xs"><strong className="text-slate-700">SEO:</strong> {analysis.seoStrengths} {analysis.seoImprovements}</p>
                <p className="text-xs"><strong className="text-slate-700">Readability:</strong> {analysis.readability}</p>
                <p className="text-xs"><strong className="text-slate-700">Persuasiveness:</strong> {analysis.persuasiveness}</p>
              </div>
            </div>
          )}

          {/* Tabbed Preview/Editor */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-slate-700">
                Generated Description
              </label>
              <div className="flex items-center rounded-lg bg-slate-200 p-0.5">
                <button
                  type="button"
                  onClick={() => setOutputViewMode('preview')}
                  className={`text-xs font-medium py-1 px-3 rounded-md flex items-center ${outputViewMode === 'preview' ? 'bg-white shadow' : 'bg-transparent text-slate-600'}`}>
                  <Eye size={14} className="mr-1" /> Preview
                </button>
                <button
                  type="button"
                  onClick={() => setOutputViewMode('html')}
                  className={`text-xs font-medium py-1 px-3 rounded-md flex items-center ${outputViewMode === 'html' ? 'bg-white shadow' : 'bg-transparent text-slate-600'}`}>
                  <Code2 size={14} className="mr-1" /> Edit HTML
                </button>
              </div>
            </div>

            {outputViewMode === 'preview' ? (
              <div
                className="prose prose-sm max-w-none bg-white p-4 rounded-lg border border-slate-300 min-h-[250px] max-h-[400px] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: bundleCopy || "<p class='text-slate-400 italic'>Your generated description will appear here...</p>" }}
              />
            ) : (
              <textarea
                id="bundle-copy"
                className="w-full min-h-[250px] rounded-lg border border-slate-300 p-3 text-sm shadow-sm focus:ring-2 focus:ring-sky-500 font-mono"
                placeholder="Your bundle description HTML will appear here..."
                value={bundleCopy}
                onChange={(e) => setBundleCopy(e.target.value)}
              />
            )}
          </div>

          {/* Refine Description Box */}
          {bundleCopy && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg">
              <button
                className="w-full flex items-center justify-between p-3 text-sm font-medium text-slate-700"
                onClick={() => setShowRefine(v => !v)}
              >
                <span className="inline-flex items-center gap-2"><RefreshCw size={16} /> Refine Description (Optional)</span>
                {showRefine ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              {showRefine && (
                <div className="p-3 pt-0 space-y-2">
                  <textarea
                    className="w-full p-2 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 text-sm"
                    rows="2"
                    placeholder="e.g., Make it more exciting, focus on 6th grade, make the hook shorter..."
                    value={rephraseRequest}
                    onChange={(e) => setRephraseRequest(e.target.value)}
                  />
                  <button
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium shadow-sm"
                    onClick={onRefine}
                    disabled={generating || !rephraseRequest}
                  >
                    {generating ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                    Refine
                  </button>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex flex-wrap items-center gap-2">
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
            onClick={handleTptCopy}
            disabled={!bundleTitle || !bundleCopy}
          >
            {copied === 'tpt' ? <Check size={16} /> : <ClipboardCopy size={16} />}
            {copied === 'tpt' ? 'Copied!' : 'Copy for TPT'}
          </button>
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-sm font-medium"
            onClick={handleBodyCopy}
            disabled={!bundleCopy}
          >
            {copied === 'body' ? <Check size={16} /> : <Copy size={16} />}
            {copied === 'body' ? 'Copied!' : 'Copy Description'}
          </button>
          <div className="flex-1" />
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium"
            onClick={onSave}
            disabled={saving || !currentUser}
            title={currentUser ? 'Save to your Bundle Plans' : 'Log in to save plans'}
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save Plan'}
          </button>
        </div>
      </div>
    </div>
  );
};


/* *******************************************************************
 * --- Main BundleComposer (The Wizard Controller) ---
 ******************************************************************* */
const BundleComposer = ({
  products = [],
  currentUser = null,
  tone = 'Clear & Professional',
  audience = 'teachers',
  subject = '',
  gradeBand = '',
  descriptionLength = 'medium',
  onSaved,
}) => {
  // ------ Wizard State ------
  const [currentStep, setCurrentStep] = useState(1);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [suggestionLimit, setSuggestionLimit] = useState(SUGGESTION_INITIAL_LIMIT);

  // ------ Filter State ------
  const [minSize, setMinSize] = useState(2);
  const [maxSize, setMaxSize] = useState(4);
  const [gradeFilter, setGradeFilter] = useState([]);
  const [subjectFilter, setSubjectFilter] = useState([]);
  const [seasonFilter, setSeasonFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);

  // ------ Builder State ------
  const [selected, setSelected] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bundleTitle, setBundleTitle] = useState('');
  const [bundleCopy, setBundleCopy] = useState('');
  const [showUrls, setShowUrls] = useState(true);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [generatedAnalysis, setGeneratedAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [rephraseRequest, setRephraseRequest] = useState('');

  // ------ Data/Context State ------
  const [savedPlans, setSavedPlans] = useState([]);
  const [savedPlansErr, setSavedPlansErr] = useState(null);
  const [savedSearch, setSavedSearch] = useState('');
  const [dismissedKeys, setDismissedKeys] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem('bundleDismissedKeys');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  // --- NEW: State for guide modal ---
  const [showGuideModal, setShowGuideModal] = useState(false);
  // --- NEW: State for interactive modal ---
  const [showInteractiveModal, setShowInteractiveModal] = useState(false);

  // --- NEW: Add Storylane script on mount ---
  useEffect(() => {
    // Check if script is already added
    if (document.querySelector(`script[src="${STORYLANE_SCRIPT_URL}"]`)) {
      return;
    }

    const script = document.createElement('script');
    script.src = STORYLANE_SCRIPT_URL;
    script.async = true;
    document.body.appendChild(script);
  }, []); // Empty dependency array ensures this runs once on mount


  // ------ Handlers & Logic ------
  const persistDismissedKeys = (keys) => {
    try { if (typeof window !== 'undefined') { window.localStorage.setItem('bundleDismissedKeys', JSON.stringify(keys)); } }
    catch { /* ignore */ }
  };

  const handleDismissSuggestion = (bundle) => {
    const key = makeBundleKey(bundle);
    if (!key) return;
    setDismissedKeys(prev => {
      if (prev.includes(key)) return prev;
      const next = [...prev, key];
      persistDismissedKeys(next);
      return next;
    });
  };

  useEffect(() => {
    if (minSize < 2) setMinSize(2);
    if (maxSize < 2) setMaxSize(2);
    if (minSize > maxSize) setMinSize(maxSize);
    if (maxSize < minSize) setMaxSize(minSize);
  }, [minSize, maxSize]);

  // ------ Product derived data ------
  const productsWithBuckets = useMemo(() => (products || []).map(p => ({ ...p, __buckets: getBuckets(p) })), [products]);
  const allGrades = useMemo(() => Array.from(new Set(productsWithBuckets.flatMap(p => Array.from(p.__buckets.grades)))).sort(), [productsWithBuckets]);
  const allSubjects = useMemo(() => Array.from(new Set(productsWithBuckets.flatMap(p => Array.from(p.__buckets.subjects)))).sort(), [productsWithBuckets]);
  const allSeasons = useMemo(() => Array.from(new Set(productsWithBuckets.flatMap(p => Array.from(p.__buckets.seasonal)))).sort(), [productsWithBuckets]);
  const allTypes = useMemo(() => Array.from(new Set(productsWithBuckets.flatMap(p => Array.from(p.__buckets.types)))).sort(), [productsWithBuckets]); // FIX: Typo corrected here

  const filteredProducts = useMemo(() => {
    let base = productsWithBuckets;
    if (gradeFilter.length)   base = base.filter(p => gradeFilter.every(g => p.__buckets.grades.has(g)));
    if (subjectFilter.length) base = base.filter(p => subjectFilter.every(s => p.__buckets.subjects.has(s)));
    if (seasonFilter.length)  base = base.filter(p => seasonFilter.every(s => p.__buckets.seasonal.has(s)));
    if (typeFilter.length)    base = base.filter(p => typeFilter.every(t => p.__buckets.types.has(t)));
    return base;
  }, [productsWithBuckets, gradeFilter, subjectFilter, seasonFilter, typeFilter]);

  const hasActiveFilters = gradeFilter.length > 0 || subjectFilter.length > 0 || seasonFilter.length > 0 || typeFilter.length > 0;

  const suggestions = useMemo(() => {
    if (currentStep !== 2) return [];

    const base = proposeBundles(filteredProducts, { minSize, maxSize });

    const decorated = base.map(s => {
      const titleCase = (str) => str.replace(/\b([a-z])/g, m => m.toUpperCase());
      const buckets = s.products.map(p => (p.__buckets ? p.__buckets : getBuckets(p)));

      const intersectManyLower = (sets) => {
        if (!sets || !sets.length) return new Set();
        const [first, ...rest] = sets;
        const out = new Set(first);
        for (const s of rest) {
          for (const v of Array.from(out)) {
            if (!s.has(v)) out.delete(v);
          }
        }
        return out;
      };

      const subjectsSets = buckets.map(b => b.subjects || new Set());
      const gradesSets   = buckets.map(b => b.grades || new Set());
      const seasonalSets = buckets.map(b => b.seasonal || new Set());
      const typesSets    = buckets.map(b => b.types || new Set());
      const common = {
        subjects: intersectManyLower(subjectsSets), grades: intersectManyLower(gradesSets),
        seasonal: intersectManyLower(seasonalSets), types: intersectManyLower(typesSets),
      };
      const matchTags = {
        subjects: Array.from(common.subjects).map(titleCase),
        grades:   Array.from(common.grades).map(prettyGrade),
        seasonal: Array.from(common.seasonal).map(titleCase),
        types:    Array.from(common.types).map(titleCase),
      };
      const tagMatchCount = matchTags.subjects.length + matchTags.grades.length + matchTags.seasonal.length + matchTags.types.length;
      const boostedScore = (s.score || 0) + tagMatchCount * 2;

      return { ...s, matchTags, tagMatchCount, score: boostedScore, why: buildWhyItWorks({ ...s, matchTags, products: s.products }) };
    });

    const afterDismiss = decorated.filter(b => !dismissedKeys.includes(makeBundleKey(b)));
    const tagged = afterDismiss.filter(s => s.tagMatchCount > 0);
    const pool = tagged.length ? tagged : afterDismiss;

    return pool
      .sort((a, b) => ((b.score || 0) - (a.score || 0)) || (b.tagMatchCount - a.tagMatchCount) || (a.products.length - b.products.length))
      .slice(0, SUGGESTION_TOTAL_LIMIT);
  }, [filteredProducts, minSize, maxSize, dismissedKeys, currentStep]);

  const selectedProducts = useMemo(() => productsWithBuckets.filter(p => selected.includes(p.id)), [productsWithBuckets, selected]);

  // Saved bundles loader
  useEffect(() => {
    const load = async () => {
      setSavedPlansErr(null);
      if (!currentUser) { setSavedPlans([]); return; }
      try {
        const col = collection(db, 'users', currentUser.uid, 'savedDescriptions');
        const qy = query(col, where('kind', '==', 'bundle'), orderBy('createdAt', 'desc'), limit(SAVED_QUERY_LIMIT));
        const snap = await getDocs(qy);
        setSavedPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        setSavedPlans([]); setSavedPlansErr(e.message || 'Permission denied');
        console.warn('Load saved bundles failed:', e);
        if (e.code === 'failed-precondition') {
          setSavedPlansErr("The database query needs an index. Please check the console log for a link to create it in Firebase.");
          console.error("FIRESTORE INDEX ERROR: ", e.message);
        }
      }
    };
    load();
  }, [currentUser]);

  const handleLoadPlan = (plan) => {
    if (!plan) return;
    setSelected(plan.productIds || []);
    setBundleTitle(plan.title || '');
    setBundleCopy(plan.body || '');
    setShowUrls(plan.includeUrls ?? true);
    setRelatedProducts(plan.relatedProductIds || []);
    setCurrentPlanId(plan.id);
    setGeneratedAnalysis(null);
    setRephraseRequest('');
    setIsBuilderOpen(true);
  };

  const handleUseSuggestion = (bundle) => {
    setSelected(bundle.products.map(p => p.id));
    setBundleTitle(createBundleTitle(bundle, { subject, gradeBand }));
    setBundleCopy('');
    setRelatedProducts([]);
    setCurrentPlanId(null);
    setGeneratedAnalysis(null);
    setRephraseRequest('');
    setIsBuilderOpen(true);
  };

  const handleAddProduct = (id) => {
    if (!selected.includes(id)) {
      setSelected(prev => [...prev, id]);
    }
  };

  const removeSelected = (id) => {
    const newSelected = selected.filter(x => x !== id);
    setSelected(newSelected);
    if (newSelected.length < 2) {
      setIsBuilderOpen(false);
      setCurrentPlanId(null);
    }
  };

  // ------ Generate / Export / Save (Passed to Modal) ------
  const handleGenerateAnalysis = async (description, title) => {
    setIsAnalyzing(true);
    setGeneratedAnalysis(null);
    try {
      const productList = selectedProducts.map(p => p.productTitle || p.title);
      const prompt = buildDescriptionAnalysisPrompt(description, title, productList);
      const callGeminiCloudFunction = httpsCallable(functionsInstance, 'callGeminiAPI');
      const result = await callGeminiCloudFunction({ prompt, model: "gemini-2.5-flash-lite" });
      const rawText = result.data?.generatedText || '{}';
      const cleanedText = rawText.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(cleanedText);
      setGeneratedAnalysis(parsed);
    } catch (e) {
      console.error("Error generating analysis:", e);
      setGeneratedAnalysis(null);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGenerate = async () => {
    const picked = selectedProducts;
    if (picked.length < 2) return alert('Select at least two products.');
    if (!currentUser) {
      alert("Please log in to generate descriptions.");
      return;
    }
    setGenerating(true);
    setGeneratedAnalysis(null);
    setIsAnalyzing(false);

    try {
      const relatedProductObjects = (relatedProducts || [])
        .map(id => productsWithBuckets.find(p => p.id === id))
        .filter(Boolean);

      const { systemPrompt, userPrompt } = buildBundlePrompts({
        products: picked,
        title: bundleTitle || createBundleTitle({ products: picked, tags: [], score: 0, level: 'good' }, { subject, gradeBand }),
        tone, audience, subject, gradeBand, descriptionLength,
        includeUrls: showUrls,
        relatedProducts: relatedProductObjects
      });

      const fullPrompt = `${systemPrompt}\n\n${userPrompt}`;
      const callGeminiCloudFunction = httpsCallable(functionsInstance, 'callGeminiAPI');

      const result = await callGeminiCloudFunction({
        prompt: fullPrompt,
        model: "gemini-2.5-flash-lite"
      });

      const text = result.data?.generatedText;
      if (!text) {
        throw new Error("Received an empty response from the AI.");
      }

      const cleanText = text.trim();
      setBundleCopy(cleanText);

      await handleGenerateAnalysis(cleanText, bundleTitle);

      try { if (analytics) logEvent(analytics, 'bundle_generate', { count: picked.length, with_urls: showUrls ? 'y' : 'n' }); } catch { }
    } catch (e) {
      console.error("Error calling callGeminiAPI:", e);
      alert(`Error generating description: ${e.message || 'An unknown error occurred.'} (Code: ${e.code || 'N/A'})`);
    } finally {
      setGenerating(false);
    }
  };

  const handleRefineDescription = async () => {
    if (!rephraseRequest.trim()) return alert("Please enter a refinement request.");
    if (!bundleCopy.trim()) return alert("Please generate a description first before refining.");

    setGenerating(true);
    setGeneratedAnalysis(null);
    setIsAnalyzing(false);

    try {
      const { systemPrompt, userPrompt } = buildRefinePrompt({
        currentTitle: bundleTitle,
        currentDescription: bundleCopy,
        rephraseRequest: rephraseRequest,
      });

      const fullPrompt = `${systemPrompt}\n\n${userPrompt}`;
      const callGeminiCloudFunction = httpsCallable(functionsInstance, 'callGeminiAPI');

      const result = await callGeminiCloudFunction({
        prompt: fullPrompt,
        model: "gemini-2.5-flash-lite"
      });

      const text = result.data?.generatedText;
      if (!text) {
        throw new Error("Received an empty response from the AI.");
      }

      const cleanText = text.trim();
      setBundleCopy(cleanText);
      setRephraseRequest('');

      await handleGenerateAnalysis(cleanText, bundleTitle);

    } catch (e) {
      console.error("Error refining description:", e);
      alert(`Error refining description: ${e.message || 'An unknown error occurred.'} (Code: ${e.code || 'N/A'})`);
    } finally {
      setGenerating(false);
    }
  };

  const handleDeletePlan = async (planId, planTitle) => {
    if (!currentUser) return;
    if (!window.confirm(`Are you sure you want to delete the bundle "${planTitle}"? This cannot be undone.`)) {
      return;
    }
    try {
      const docRef = doc(db, 'users', currentUser.uid, 'savedDescriptions', planId);
      await deleteDoc(docRef);
      setSavedPlans(prev => prev.filter(p => p.id !== planId));
      alert("Bundle plan deleted.");
    } catch (e) {
      console.error("Error deleting bundle plan:", e);
      alert(`Failed to delete plan: ${e.message}`);
    }
  };

  const handleSavePlan = async () => {
    const picked = selectedProducts;
    if (!currentUser) return alert('Please log in to save bundle plans.');
    if (picked.length < 2) return alert('Select at least two products to save a plan.');
    const title = (bundleTitle || '').trim();
    if (!title) return alert('Please enter a bundle title before saving.');
    setSaving(true);

    const planData = {
      kind: 'bundle', title,
      productIds: picked.map(p => p.id),
      productTitles: picked.map(p => p.productTitle || p.title || 'Untitled'),
      body: bundleCopy || '',
      includeUrls: showUrls,
      relatedProductIds: relatedProducts || [],
      tone, audience, subject, gradeBand, descriptionLength,
    };

    try {
      let docRef;
      if (currentPlanId) {
        console.log(`Updating existing plan: ${currentPlanId}`);
        docRef = doc(db, 'users', currentUser.uid, 'savedDescriptions', currentPlanId);
        await setDoc(docRef, {
          ...planData,
          updatedAt: serverTimestamp()
        }, { merge: true });

      } else {
        console.log("Creating new plan...");
        const collectionRef = collection(db, 'users', currentUser.uid, 'savedDescriptions');
        docRef = await addDoc(collectionRef, {
          ...planData,
          createdAt: serverTimestamp(),
        });
        setCurrentPlanId(docRef.id);
      }

      try { if (analytics) logEvent(analytics, 'bundle_plan_saved', { items: picked.length, is_update: !!currentPlanId }); } catch { }
      onSaved?.(docRef.id);
      alert('Bundle plan saved!');

      const col = collection(db, 'users', currentUser.uid, 'savedDescriptions');
      const qy = query(col, where('kind', '==', 'bundle'), orderBy('createdAt', 'desc'), limit(SAVED_QUERY_LIMIT));
      const snap = await getDocs(qy);
      setSavedPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));

    } catch (e) {
      console.error('Save bundle plan failed:', e);
      if (e.code === 'failed-precondition') {
        alert(`Failed to save: ${e.message}. You must create the Firestore index. Check the console for the link.`);
        console.error("FIRESTORE INDEX ERROR: ", e.message);
      } else {
        alert(`Failed to save bundle plan: ${e.message} (Code: ${e.code}).`);
      }
    } finally {
      setSaving(false);
    }
  };

  /* ======================= RENDER ======================= */

  return (
    // --- WRAPPED IN FRAGMENT TO ALLOW MODAL SIBLING ---
    <>
      <div className="max-w-7xl mx-auto space-y-6 font-sans">

        {/* --- Intro / How-to --- */}
        <div className="relative bg-white border border-slate-200 rounded-2xl p-6 shadow-lg flex items-start gap-4">

          {/* --- MODIFIED BUTTON CONTAINER --- */}
          <div className="absolute top-6 right-6 flex items-center gap-2">
            <button
                type="button"
                onClick={() => {
                    setShowInteractiveModal(true);
                    if (analytics) {
                        logEvent(analytics, 'guide_click', {
                            content_type: 'interactive_walkthrough',
                            item_id: 'bundle_creator_tool_interactive'
                        });
                    }
                }}
                className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"
            >
                <MousePointerClick size={14} />
                Interactive Walkthrough
            </button>

            <button
                type="button"
                onClick={() => {
                    setShowGuideModal(true);
                    if (analytics) {
                        logEvent(analytics, 'guide_click', {
                            content_type: 'how_to_guide',
                            item_id: 'bundle_creator_tool_guide'
                        });
                    }
                }}
                className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"
            >
                <Video size={14} />
                View Guide
            </button>
          </div>
          {/* --- END MODIFIED BUTTON CONTAINER --- */}

          <Lightbulb size={24} className="text-sky-600 shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Welcome to the Bundle Wizard!</h3>
            <p className="text-sm text-slate-500 mt-1">
              Just follow the steps to find and create the perfect bundle in minutes.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* --- Main Column (Wizard Steps) --- */}
          <div className="lg:col-span-2">

            {/* *****************************
             * --- STEP 1: FILTERS ---
             ***************************** */}
            <div className={`bg-white border border-slate-200 rounded-2xl shadow-lg ${currentStep === 1 ? 'block' : 'hidden'}`}>
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-xl font-semibold text-sky-700 inline-flex items-center gap-2">
                  <span className="text-sm font-bold bg-sky-600 text-white rounded-full w-6 h-6 flex items-center justify-center">1</span>
                  Find Matching Products
                  <Info size={14} className="text-slate-400" title="Select tags that your products share. We'll find products that match ALL selected filters."/>
                </h2>
                <p className="text-sm text-slate-500 mt-2 pl-8">
                  Start by choosing one or more filters. We'll find all your products that match.
                </p>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MultiSelect options={allGrades} selected={gradeFilter} onChange={setGradeFilter} placeholder="Filter by Grade..." />
                  <MultiSelect options={allSubjects} selected={subjectFilter} onChange={setSubjectFilter} placeholder="Filter by Subject..." />
                  <MultiSelect options={allSeasons} selected={seasonFilter} onChange={setSeasonFilter} placeholder="Filter by Season..." />
                  <MultiSelect options={allTypes} selected={typeFilter} onChange={setTypeFilter} placeholder="Filter by Type..." />
                </div>

                <div className="pt-4 border-t border-slate-100 flex items-center gap-4">
                  <span className="text-sm text-slate-700 font-medium">Bundle size:</span>
                  <Info size={14} className="text-slate-400" title="We'll look for bundles within this size range."/>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-600">Min</label>
                    <select value={minSize} onChange={e => setMinSize(Number(e.target.value))} className="text-sm rounded-md border-slate-300 shadow-sm focus:ring-2 focus:ring-sky-500">
                      {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-600">Max</label>
                    <select value={maxSize} onChange={e => setMaxSize(Number(e.target.value))} className="text-sm rounded-md border-slate-300 shadow-sm focus:ring-2 focus:ring-sky-500">
                      {[3, 4, 5, 6, 8, 10, 15, 20, 30, 50, 90].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl text-right">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!hasActiveFilters}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold shadow-md transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed"
                  title={!hasActiveFilters ? "Please select at least one filter to start" : "Find bundles"}
                >
                  Find Bundle Ideas
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>

            {/* *****************************
             * --- STEP 2: SUGGESTIONS ---
             ***************************** */}
            <div className={`${currentStep === 2 ? 'block' : 'hidden'}`}>
              <div className="bg-white border border-slate-200 rounded-2xl shadow-lg">
                <div className="p-6 border-b border-slate-200">
                  <h2 className="text-xl font-semibold text-sky-700 inline-flex items-center gap-2">
                    <span className="text-sm font-bold bg-sky-600 text-white rounded-full w-6 h-6 flex items-center justify-center">2</span>
                    Review Smart Suggestions
                    <Info size={14} className="text-slate-400" title="These are the best matches based on your filters. 'Strong' matches have the most shared tags."/>
                  </h2>
                  <p className="text-sm text-slate-500 mt-2 pl-8">
                    Found <b className="text-slate-800">{filteredProducts.length}</b> matching products. Here are the best bundle ideas.
                  </p>
                </div>

                {suggestions.length === 0 ? (
                  <div className="p-6 text-center">
                    <Search size={32} className="mx-auto text-slate-400" />
                    <p className="mt-2 text-base font-medium text-slate-700">No suggestions found</p>
                    <p className="text-sm text-slate-500">
                      Your {filteredProducts.length} matching products don't seem to have strong tag overlaps. Try adjusting your filters in Step 1 for a wider search.
                    </p>
                  </div>
                ) : (
                  <ul className="p-6 space-y-4">
                    {suggestions.slice(0, suggestionLimit).map((s, idx) => (
                      <SuggestionCard
                        key={idx}
                        suggestion={s}
                        onUse={handleUseSuggestion}
                      />
                    ))}
                  </ul>
                )}

                <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex items-center justify-between">
                  <button
                    onClick={() => {
                      setCurrentStep(1);
                      setSuggestionLimit(SUGGESTION_INITIAL_LIMIT);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium shadow-sm"
                  >
                    <ArrowLeft size={16} />
                    Back to Filters
                  </button>

                  {suggestions.length > suggestionLimit && (
                    <button
                      onClick={() => setSuggestionLimit(SUGGESTION_TOTAL_LIMIT)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sky-600 hover:bg-sky-50 text-sm font-medium"
                    >
                      <ListPlus size={16} />
                      Show More ({suggestions.length - suggestionLimit} more)
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* --- Sidebar Column (Saved Bundles) --- */}
          <div className="lg:col-span-1 space-y-6">
            <SavedBundlesPanel
              savedPlans={savedPlans}
              error={savedPlansErr}
              onLoadPlan={handleLoadPlan}
              onDeletePlan={handleDeletePlan}
              savedSearch={savedSearch}
              setSavedSearch={setSavedSearch}
            />
          </div>
        </div>

        {/* *****************************
         * --- STEP 3: BUILDER MODAL ---
         ***************************** */}
        <BuilderModal
          isOpen={isBuilderOpen}
          onClose={() => {
            setIsBuilderOpen(false);
            setCurrentPlanId(null);
          }}
          selectedProducts={selectedProducts}
          onRemove={removeSelected}
          bundleTitle={bundleTitle}
          setBundleTitle={setBundleTitle}
          bundleCopy={bundleCopy}
          setBundleCopy={setBundleCopy}
          generating={generating}
          saving={saving}
          onGenerate={handleGenerate}
          onSave={handleSavePlan}
          showUrls={showUrls}
          setShowUrls={setShowUrls}
          currentUser={currentUser}
          filteredProducts={filteredProducts}
          allProducts={productsWithBuckets}
          onAddProduct={handleAddProduct}
          analysis={generatedAnalysis}
          isAnalyzing={isAnalyzing}
          relatedProducts={relatedProducts}
          setRelatedProducts={setRelatedProducts}
          onRefine={handleRefineDescription}
          rephraseRequest={rephraseRequest}
          setRephraseRequest={setRephraseRequest}
        />
      </div>

      {/* --- GUIDE MODAL --- */}
      {showGuideModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowGuideModal(false)} // Close on overlay click
        >
          <div
            className="w-full max-w-5xl h-[90vh] rounded-xl bg-slate-800 border border-slate-700 shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()} // Prevent click from bubbling to overlay
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-slate-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-sky-400">
                Bundle Creator Tool Guide
              </h3>
              <button
                type="button"
                onClick={() => setShowGuideModal(false)}
                className="text-slate-300 hover:text-white p-1 rounded-full hover:bg-slate-700"
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body - Iframe */}
            <div className="flex-1 p-0 md:p-4 overflow-hidden">
              <iframe
                src="https://scribehow.com/embed/How_To_Use_The_Bundles_Creation_Tool__4rN3W8aLQZuwD4dy3SFEuw?as=scrollable"
                width="100%"
                height="100%"
                allow="fullscreen"
                style={{ border: 0, minHeight: '640px' }}
                title="Bundle Creator Tool Guide"
              ></iframe>
            </div>
          </div>
        </div>
      )}
      {/* --- END GUIDE MODAL --- */}

      {/* --- NEW INTERACTIVE WALKTHROUGH MODAL --- */}
      {showInteractiveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowInteractiveModal(false)}
        >
          {/* MODIFIED: Use max-w-7xl for a wider modal */}
          <div
            className="w-full max-w-7xl rounded-xl bg-slate-800 border border-slate-700 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-slate-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-sky-400">
                Interactive Walkthrough
              </h3>
              <button
                type="button"
                onClick={() => setShowInteractiveModal(false)}
                className="text-slate-300 hover:text-white p-1 rounded-full hover:bg-slate-700"
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
            </div>

            {/* MODIFIED: Increased padding-bottom to give more vertical space */}
            <div
              className="sl-embed"
              style={{
                position: 'relative',
                paddingBottom: 'calc(55% + 25px)', // Increased height ratio slightly
                width: '100%',
                height: 0,
                transform: 'scale(1)',
                background: '#1e293b'
              }}
            >
              <iframe
                loading="lazy"
                className="sl-demo"
                src="https://app.storylane.io/demo/lbwihnjdikeo?embed=popup"
                name="sl-embed"
                allow="fullscreen"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: '1px solid rgba(63,95,172,0.35)',
                  boxShadow: '0px 0px 18px rgba(26, 19, 72, 0.15)',
                  borderRadius: '10px',
                  boxSizing: 'border-box'
                }}
                title="Interactive Walkthrough"
              ></iframe>
            </div>
          </div>
        </div>
      )}
      {/* --- END INTERACTIVE WALKTHROUGH MODAL --- */}
    </>
  );
};

export default BundleComposer;